import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import { setScrollLock } from "@/lib/scroll";
import { EASE } from "@/components/Shared";

const LINKS = [
  { id: "home", path: "/", label: "Home" },
  { id: "services", path: "/services", label: "Services" },
  { id: "work", path: "/work", label: "Our Work" },
  { id: "about", path: "/about", label: "About" },
  { id: "contact", path: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const active = location.pathname === "/" ? "home" : location.pathname.slice(1);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setScrollLock(open);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      setScrollLock(false);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
        data-testid="nav-header"
        className={`fixed inset-x-0 top-0 z-[60] transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled || active !== "home"
            ? "border-b border-white/10 bg-ink/80 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="container-x flex h-[72px] items-center justify-between">
          <Logo onClick={() => go("/")} />

          <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
            {LINKS.map(({ id, path, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => go(path)}
                data-testid={`nav-link-${id}`}
                className="relative rounded-full px-4 py-2 text-sm text-sub transition-colors duration-300 hover:text-txt"
              >
                {active === id && (
                  <motion.span
                    layoutId="nav-pill"
                    className="absolute inset-0 rounded-full border border-white/10 bg-white/[0.06]"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                <span className={`relative ${active === id ? "text-txt" : ""}`}>{label}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => go("/contact")}
              data-testid="nav-cta-button"
              className="btn-primary ml-4 !px-6 !py-2.5"
            >
              Start a Project
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
            </button>
          </nav>

          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="relative z-[60] flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] lg:hidden"
          >
            <span
              className={`absolute h-px w-5 bg-txt transition-all duration-300 ${
                open ? "rotate-45" : "-translate-y-1.5"
              }`}
            />
            <span
              className={`absolute h-px w-5 bg-txt transition-all duration-300 ${
                open ? "-rotate-45" : "translate-y-1.5"
              }`}
            />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="mobile-menu-panel"
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.55, ease: EASE }}
            className="fixed inset-0 z-[55] flex flex-col justify-between bg-ink/95 px-6 pb-10 pt-28 backdrop-blur-2xl lg:hidden"
          >
            <nav aria-label="Mobile" className="flex flex-col gap-2">
              {LINKS.map(({ id, path, label }, i) => (
                <motion.button
                  key={id}
                  type="button"
                  onClick={() => go(path)}
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 + i * 0.07, duration: 0.6, ease: EASE }}
                  className="flex items-baseline gap-4 border-b border-white/5 py-4 text-left"
                >
                  <span className="font-mono text-xs text-cy">0{i + 1}</span>
                  <span className="font-grotesk text-3xl font-semibold tracking-tight text-txt">
                    {label}
                  </span>
                </motion.button>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col gap-4"
            >
              <button type="button" onClick={() => go("/contact")} className="btn-primary w-full justify-center">
                Start a Project
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
              </button>
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-sub">
                Welcome To BT Design
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
